import { useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon, ArrowUp, ArrowDown } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Upload an image and have the backend auto-fit it to 2560×1080 (hero spec).
async function uploadAndFitHero(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API_URL}/cms/hero/upload-fit`, fd, {
    headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export function ImageUpload({ value, onChange, label = "Image", folder = "images", testId = "image-upload" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setUploading(true);
    try {
      const result = await uploadFile(file, folder);
      const url = result.url.startsWith("http") ? result.url : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {value && (
        <div className="relative inline-block">
          <img src={value} alt={label} className="h-24 w-auto rounded-md border object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600" data-testid={`${testId}-remove`}><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload..." className="flex-1 text-sm" data-testid={`${testId}-url`} />
        <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`${testId}-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
        </Button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </div>
    </div>
  );
}

export function VideoUpload({ value, onChange, label = "Video", folder = "videos", testId = "video-upload" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please select a video file (MP4, MOV, etc.)"); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error("Video must be under 100MB"); return; }
    setUploading(true);
    try {
      const result = await uploadFile(file, folder);
      const url = result.url.startsWith("http") ? result.url : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      onChange(url);
      toast.success("Video uploaded");
    } catch (err) { toast.error("Video upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {value && (
        <div className="relative inline-block">
          <video src={value} className="h-32 w-auto rounded-md border object-cover" muted />
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600" data-testid={`${testId}-remove`}><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Video URL or upload MP4..." className="flex-1 text-sm" data-testid={`${testId}-url`} />
        <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`${testId}-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Upload</>}
        </Button>
        <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={handleUpload} />
      </div>
    </div>
  );
}

export function MultiImageUpload({ images = [], onChange, max = 5, label = "Images", folder = "images", testId = "multi-image-upload", autoFitHero = false }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (images.length >= max) { toast.error(`Maximum ${max} images allowed`); return; }
    setUploading(true);
    try {
      const result = autoFitHero ? await uploadAndFitHero(file) : await uploadFile(file, folder);
      const url = result.url.startsWith("http") ? result.url : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      onChange([...images, url]);
      toast.success(autoFitHero ? "Image uploaded & resized to 2560×1080" : "Image uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));
  const moveImage = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= images.length) return;
    const arr = [...images];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    onChange(arr);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{label} ({images.length}/{max})</span>
      </div>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group">
              <img src={img} alt={`${label} ${idx + 1}`} className="h-20 w-20 rounded-md border object-cover" />
              <button type="button" onClick={() => removeImage(idx)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testId}-remove-${idx}`}><X className="w-3 h-3" /></button>
              <div className="absolute bottom-0 left-0 right-0 flex justify-between px-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" disabled={idx === 0} onClick={() => moveImage(idx, -1)} className="bg-black/60 text-white rounded p-0.5 disabled:opacity-30" data-testid={`${testId}-move-up-${idx}`}><ArrowUp className="w-3 h-3" /></button>
                <span className="bg-black/60 text-white text-[10px] px-1 rounded self-center">{idx + 1}</span>
                <button type="button" disabled={idx === images.length - 1} onClick={() => moveImage(idx, 1)} className="bg-black/60 text-white rounded p-0.5 disabled:opacity-30" data-testid={`${testId}-move-down-${idx}`}><ArrowDown className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {images.length < max && (
        <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`${testId}-add-btn`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
          Add Image ({images.length}/{max})
        </Button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}
